import { Http, HttpRequestConfig, HttpInstance } from '@okapi/http';
import { ApiException } from './api-exeption';

export type ApiRequestConfig = HttpRequestConfig & {
  orchestratorHost: string;
  useCredentials: boolean;
  requestID: string;
  apiKey?: string;
};

const SUCCESS_RESPONSE_STATUS_CODE_LIST = [200, 201, 202];

export class Api {
  http: HttpInstance;

  constructor() {
    this.http = new Http({
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json;charset=UTF-8',
      },
    });
  }

  public abortController = () => this.http.abortController();

  post = <Response = unknown, Data = unknown>(url: string, data: Data, config: ApiRequestConfig) =>
    this._doFetch<Response>(buildUrl(config.orchestratorHost, url), {
      method: 'POST',
      data,
      headers: config?.headers,
      ...config,
    });

  get = <Response = unknown, Data extends Record<string, string> = Record<string, string>>(
    url: string,
    data: Data,
    config: ApiRequestConfig
  ) => {
    const params = new URLSearchParams(data).toString();
    const resultUrl = `${buildUrl(config.orchestratorHost, url)}?${params}`;

    return this._doFetch<Response>(resultUrl, { method: 'GET', ...config });
  };

  private _doFetch<Response>(url: string, config: ApiRequestConfig): Promise<Response> {
    const resultUrl = /^\/\//.test(url) ? `https:${url}` : url;
    const { useCredentials, requestID, apiKey, ...init } = config;

    init.withCredentials = useCredentials;

    if (requestID) {
      init.headers = { ...init.headers, 'x-request-id': requestID };
    }

    if (apiKey) {
      init.headers = { ...init.headers, 'x-api-key': apiKey };
    }

    return this.http
      .request<Response>(resultUrl, init)
      .then(response =>
        SUCCESS_RESPONSE_STATUS_CODE_LIST.includes(response.status) ? response : Promise.reject(response)
      )
      .then(response => response.data)
      .catch(errorResponse => {
        if (errorResponse.response) {
          throw new ApiException(errorResponse.response);
        }
        throw new ApiException(errorResponse);
      });
  }
}

export const api = new Api();

function buildUrl(base: string, part: string) {
  return new URL(part, base).toString();
}
